import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { isUndefined, isEqual, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Accordion, SelectDataInTable } from '~/components';
import { Select, Input } from '~/components/Form';
import { MODULO_20_GRAUS, SKID, DESCARGA_NF } from '~/constants/modules';
import { t, validCharacters, can, number } from '~/helpers';
import { storeProduto, updateProduto } from '~/store/modules/produto/actions';

import * as S from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const [tipoProduto, setTipoProduto] = useState('C');
  const [confirmaTrocaLubrificante, setConfirmaTrocaLubrificante] = useState(
    'S'
  );
  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.produto);
  const [produtonfes, setProdutonfes] = useState([]);
  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  async function handleTipoProduto(value) {
    await reference.current.setFieldValue('tipoproduto', value);
    await setTipoProduto(value);
  }

  async function handleConfirmaTrocaLubrificante(value) {
    await reference.current.setFieldValue(
      'confirmatrocalubrificanteproduto',
      value
    );
    await setConfirmaTrocaLubrificante(value);
  }

  async function validateForm(value) {
    if (!find.produtosnfes) {
      return;
    }
    if (isEqual(value, produtonfes)) {
      return;
    }
    if (value.length === 0 && find.produtosnfes.length > 0) {
      setProdutonfes([]);
      return;
    }
    setProdutonfes(value);
  }

  const isFields = useMemo(() => [
    {
      id: 'idproduto',
      label: t('s.cadastro.modeloveiculo.idcompartimento'),
      type: 'text',
    },
    {
      id: 'fatorconversaounidadeemlitrosprodutonfe',
      label: t('s.geral.fatorconversaounidadeemlitrosprodutonfe'),

      type: 'number',
    },
  ]);

  const cellsCompartiments = useMemo(() => [
    {
      id: 'idproduto',
      label: t('s.cadastro.modeloveiculo.idcompartimento'),
    },
    {
      id: 'fatorconversaounidadeemlitrosprodutonfe',
      label: t('s.geral.fatorconversaounidadeemlitrosprodutonfe'),

      format: (value) => number(value, 1),
      align: 'right',
    },
  ]);

  const schemaValidate = useMemo(
    () => ({
      fatorconversaounidadeemlitrosprodutonfe: Yup.number()
        .typeError(t('s.geral.validacao.campoobrigatorio'))
        .required(t('s.geral.validacao.campoobrigatorio'))
        .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0)),
      idproduto: Yup.string()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .max(20, t('s.geral.validacao.valormaximo').replace('%s', 20))
        .test(
          'produto-valido',
          t('s.geral.validacao.idprodutoNFInvalido'),
          (value) => {
            if (!produtonfes || produtonfes.length === 0) {
              return true;
            }
            return (
              !produtonfes.some((item) => item.idproduto === value) ||
              value === ''
            );
          }
        ),
    }),
    [produtonfes]
  );

  const accordions = useMemo(
    () => [
      {
        id: 'produtonf',
        title: t('s.geral.cadastro.codigoprodutonf'),
        component: () => (
          <SelectDataInTable
            icons={false}
            checkbox={false}
            showPagination={false}
            paginate={99999}
            noUpdate
            size="small"
            variant="contained"
            position="row"
            onChanged={(value) => {
              validateForm(value);
            }}
            unique="idproduto"
            maxWidth="xs"
            initials={produtonfes}
            fields={isFields}
            schema={schemaValidate}
            cells={cellsCompartiments}
            titleModal={t('s.geral.cadastro.codigoprodutonf')}
          />
        ),
      },
    ],
    [produtonfes]
  );

  async function handleSubmit(param) {
    try {
      reference.current.setErrors({});

      const schema = Yup.object().shape({
        idproduto: create
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
              .max(
                2147483647,
                t('s.geral.validacao.valorminimo').replace('%s', 2147483647)
              )
          : Yup.number().nullable(),
        codigoauxiliarproduto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeproduto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeabreviadoproduto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(12, t('s.geral.validacao.tamanhomaximo').replace('%s', 12))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        tipoproduto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),

        calculodensidadeproduto:
          can(MODULO_20_GRAUS) && tipoProduto === 'C'
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const listProductsNF = produtonfes.map((item) => ({
        idcliente: client,
        idprodutonfe: item.idproduto,
        fatorconversaounidadeemlitrosprodutonfe:
          item.fatorconversaounidadeemlitrosprodutonfe,
      }));

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        tipoproduto: tipoProduto,
        calculodensidadeproduto:
          can(MODULO_20_GRAUS) || can(SKID)
            ? tipoProduto === 'C'
              ? param.calculodensidadeproduto
              : 0
            : 0,
        confirmatrocalubrificanteproduto:
          tipoProduto === 'L'
            ? confirmaTrocaLubrificante
            : 'S',
        operacaopadraolubrificanteproduto:
          tipoProduto === 'L' && confirmaTrocaLubrificante === 'N'
            ? Number(param.operacaopadraolubrificanteproduto)
            : 0,
        produtosnfes: listProductsNF,
      };
      if (create) {
        await dispatch(storeProduto(objSend, params));
      } else {
        await dispatch(updateProduto(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!isEmpty(find) && !create) {
      if (find.produtosnfes) {
        setProdutonfes(
          find.produtosnfes.map((item) => ({
            idproduto: item.idprodutonfe,
            fatorconversaounidadeemlitrosprodutonfe:
              item.fatorconversaounidadeemlitrosprodutonfe,
          }))
        );
      }
    }
  }, [find]);

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    const { tipoproduto } = initials;
    if (!isUndefined(tipoproduto)) {
      setTipoProduto(tipoproduto);
    }
    setConfirmaTrocaLubrificante(
      initials.confirmatrocalubrificanteproduto || 'S'
    );
  }, [initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idproduto"
            label={t('s.configuracao.produto.idproduto')}
            variant="outlined"
            fullWidth
            size="small"
            disabled={!create}
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="codigoauxiliarproduto"
            label={t('s.configuracao.produto.codigoauxiliarproduto')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="nomeproduto"
            label={t('s.configuracao.produto.nomeproduto')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="nomeabreviadoproduto"
            label={t('s.configuracao.produto.nomeabreviadoproduto')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Select
            name="tipoproduto"
            variant="outlined"
            label={t('s.configuracao.produto.tipoproduto')}
            options={[
              { value: 'C', label: t('s.geral.opcoes.combustivel') },
              { value: 'L', label: t('s.geral.opcoes.lubrificante') },
              { value: 'G', label: t('s.geral.opcoes.graxa') },
            ]}
            onChange={({ target }) => handleTipoProduto(target.value)}
          />

          <Input
            name="corproduto"
            label={t('s.configuracao.produto.corproduto')}
            type="color"
          />

          {tipoProduto === 'L' && (
            <Select
              name="confirmatrocalubrificanteproduto"
              variant="outlined"
              size="small"
              label={t('s.configuracao.produto.confirmatrocalubrificanteproduto')}
              initialValue={confirmaTrocaLubrificante}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
              onChange={({ target }) =>
                handleConfirmaTrocaLubrificante(target.value)
              }
            />
          )}

          {tipoProduto === 'L' && confirmaTrocaLubrificante === 'N' && (
            <Select
              name="operacaopadraolubrificanteproduto"
              variant="outlined"
              size="small"
              label={t('s.configuracao.produto.operacaopadraolubrificanteproduto')}
              initialValue={
                initials.operacaopadraolubrificanteproduto === 1 ? 1 : 0
              }
              options={[
                {
                  value: 0,
                  label: t(
                    's.configuracao.produto.operacaopadraolubrificanteproduto.remonta'
                  ),
                },
                {
                  value: 1,
                  label: t(
                    's.configuracao.produto.operacaopadraolubrificanteproduto.troca'
                  ),
                },
              ]}
            />
          )}

          {(can(MODULO_20_GRAUS) || can(SKID)) && tipoProduto === 'C' && (
            <Select
              name="calculodensidadeproduto"
              variant="outlined"
              size="small"
              label={t('s.configuracao.produto.calculodensidadeproduto')}
              options={[
                {
                  value: 0,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.naocalcula'
                  ),
                },
                {
                  value: 1,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.alcoolanidro'
                  ),
                },
                {
                  value: 2,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.alcoolhidratado'
                  ),
                },
                {
                  value: 3,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.gasolina'
                  ),
                },
                {
                  value: 4,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.diesel'
                  ),
                },
                {
                  value: 5,
                  label: t(
                    's.configuracao.produto.calculodensidadeproduto.biodiesel'
                  ),
                },
              ]}
            />
          )}
          {can(DESCARGA_NF) && (
            <S.Wrapper>
              <Accordion accordions={accordions} />
            </S.Wrapper>
          )}
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
